import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import SEO from '@/components/SEO';
import { buttonVariants } from '@/components/ui/button';
import { ArrowRight, Check, X, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { BackgroundAnimation } from '@/components/background-animation';
import { HomeFAQSection } from '@/components/landing/HomeFAQSection';
import { PricingSection } from '@/components/landing/PricingSection';
import WishlistSection from '@/components/landing/WishlistSection';
import { BlogSection } from '@/components/blog/BlogSection';
import { RelatedToolsSection } from '@/components/RelatedToolsSection';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';
import { useAuth } from '@/context/auth-context';

interface ComparisonPageProps {
  competitorName: string;
  seoTitle: string;
  description: string;
  keywords: string;
  heroTitle: string;
  comparisonPoints: {
    feature: string;
    competitor: string | boolean;
    us: string | boolean;
  }[];
  contentSnippet: string;
}

const ComparisonPage: React.FC<ComparisonPageProps> = ({
  competitorName,
  seoTitle,
  description,
  keywords,
  heroTitle,
  comparisonPoints,
  contentSnippet
}) => {
  const { user } = useAuth();
  const [wishlistedPlans, setWishlistedPlans] = useState<Set<string>>(new Set());
  const [selectedWishlistPlan, setSelectedWishlistPlan] = useState<string>('general');

  useEffect(() => {
    const saved = localStorage.getItem('fmg_wishlist');
    if (saved) setWishlistedPlans(new Set(JSON.parse(saved)));
  }, []);

  const handleSelectPlan = (planId: string) => {
    setSelectedWishlistPlan(planId);
  };

  return (
    <div className="flex flex-col overflow-hidden">
      <SEO 
        title={seoTitle}
        description={description}
        keywords={keywords}
      />
      <BackgroundAnimation />
      
      <section className="relative min-h-[60vh] flex items-center justify-center pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
             <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 border border-cyan-100 mb-8 font-sans text-[10px] uppercase tracking-[0.3em] text-cyan-600 mx-auto">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Industry Comparison</span>
            </div>
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter leading-none" dangerouslySetInnerHTML={{ __html: heroTitle }} />
            <p className="text-zinc-500 text-lg max-w-2xl mx-auto font-sans mb-12">
              Discover why Fun Circle Games is the preferred choice for creators over {competitorName}.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6">
          <div className="rounded-[2rem] overflow-hidden border border-zinc-950/10 shadow-2xl bg-white">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-100">
                  <th className="p-8 text-[10px] font-black uppercase tracking-widest text-zinc-400">Feature</th>
                  <th className="p-8 text-[10px] font-black uppercase tracking-widest text-zinc-400">{competitorName}</th>
                  <th className="p-8 text-[10px] font-black uppercase tracking-widest text-cyan-600 bg-cyan-50/50">Fun Circle Games</th>
                </tr>
              </thead>
              <tbody>
                {comparisonPoints.map((point, idx) => (
                  <tr key={idx} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50 transition-colors">
                    <td className="p-8 font-bold text-zinc-950 uppercase tracking-tight text-xs">{point.feature}</td>
                    <td className="p-8 text-zinc-500 text-sm">
                      {typeof point.competitor === 'boolean' ? (
                        point.competitor ? <Check className="text-green-500 w-5 h-5" /> : <X className="text-zinc-300 w-5 h-5" />
                      ) : (
                        point.competitor
                      )}
                    </td>
                    <td className="p-8 text-zinc-950 text-sm font-medium bg-cyan-50/20">
                       {typeof point.us === 'boolean' ? (
                        point.us ? <Check className="text-cyan-600 w-5 h-5" /> : <X className="text-red-300 w-5 h-5" />
                      ) : (
                        point.us
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
           <h2 className="text-3xl md:text-5xl font-bold font-display text-zinc-950 mb-8 uppercase tracking-tighter text-center">
             The Verdict
           </h2>
           <div className="prose prose-zinc max-w-none">
             <p className="text-zinc-500 text-lg leading-relaxed text-center mb-12">
               {contentSnippet}
             </p>
           </div>
           <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link to="/register" className={cn(buttonVariants({ size: "lg" }), "rounded-full px-12 h-16 bg-cyan-600 text-white hover:bg-cyan-700 transition-all font-bold uppercase tracking-[0.3em] text-xs font-sans")}>
                Try Fun Circle Games Free <ArrowRight className="ml-3 w-5 h-5" />
              </Link>
           </div>
        </div>
      </section>

      <PricingSection user={user} onSelectPlan={handleSelectPlan} />
      
      <WishlistSection selectedPlan={selectedWishlistPlan} />

      <HomeFAQSection />
      
      <BlogSection />

      <RelatedToolsSection />

      <DiscordCTA />

      <InternalLinksSection 
        show={['pricing', 'tutorials', 'help', 'blog', 'showcase', 'faq', 'discord', 'case-studies']}
      />
    </div>
  );
};

export default ComparisonPage;
